"use client";

import { useState } from "react";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Sidebar } from "@/components/layout/sidebar";
import { redirect } from "next/navigation";
import Image from "next/image";
import Link from "next/link";
import { AlertTriangle, Mail, ShieldAlert, Loader2 } from "lucide-react";
import api from "@/lib/api";

function LoadingScreen() {
  return (
    <div className="flex h-screen flex-col items-center justify-center bg-background">
      <div className="flex flex-col items-center space-y-4">
        <Image src="/logo.svg" alt="BuildMagic" width={200} height={59} className="h-12 w-auto" />
        <div className="size-6 animate-spin rounded-full border-2 border-muted-foreground/20 border-t-primary" />
      </div>
    </div>
  );
}

function VerificationBanners() {
  const { user } = useAuth();
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  if (!user) return null;

  async function handleResend() {
    setResending(true);
    try {
      await api.resendVerification();
      setResent(true);
    } catch {
      // ignore
    } finally {
      setResending(false);
    }
  }

  return (
    <>
      {!user.email_verified && (
        <div className="flex items-center gap-3 border-b border-amber-500/30 bg-amber-500/10 px-4 py-2.5 text-sm">
          <Mail className="size-4 shrink-0 text-amber-500" />
          <span className="text-amber-200">
            Please verify your email address.{" "}
            {resent ? (
              <span className="text-green-400">Verification email sent!</span>
            ) : (
              <button
                onClick={handleResend}
                disabled={resending}
                className="font-medium underline hover:no-underline"
              >
                {resending ? "Sending..." : "Resend verification email"}
              </button>
            )}
          </span>
        </div>
      )}
      {user.email_verified && !user.totp_enabled && (
        <div className="flex items-center gap-3 border-b border-amber-500/30 bg-amber-500/10 px-4 py-2.5 text-sm">
          <ShieldAlert className="size-4 shrink-0 text-amber-500" />
          <span className="text-amber-200">
            Enable two-factor authentication to start builds.{" "}
            <Link href="/settings" className="font-medium underline hover:no-underline">
              Set up 2FA
            </Link>
          </span>
        </div>
      )}
    </>
  );
}

function DashboardContent({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) return <LoadingScreen />;
  if (!user) {
    redirect("/login");
    return null;
  }

  return (
    <div className="flex h-screen">
      <Sidebar />
      <main className="flex-1 overflow-auto">
        <VerificationBanners />
        <div className="container mx-auto p-6">{children}</div>
      </main>
    </div>
  );
}

export default function DashboardLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <AuthProvider>
      <DashboardContent>{children}</DashboardContent>
    </AuthProvider>
  );
}
